import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { KeineDrohneLeer } from '../components/LeerZustand'
import { Select, useConnectionRetry } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { MapContainer, TileLayer, Marker, Polyline, CircleMarker, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { ChevronDown, Video, VideoOff, Camera, Crosshair, RotateCcw, Thermometer, Navigation, Orbit, ZoomIn, Map as MapIcon, Hand, ShieldOff, Home, ArrowDownToLine, Square, X, OctagonX } from 'lucide-react'
import { LivebildPlayer, FreigabeAnzeige, type LivebildInfo, type UebertragungsInfo } from '../components/Livebild'
import { liveDrohneSvg } from '../components/taktischeSymbolUi'
import { HandsteuerungPanel, TouchSticks } from '../components/cockpit/Handsteuerung'
import { useHandsteuerung } from '../components/cockpit/useHandsteuerung'

// Cockpit für den Co-Piloten (ersetzt die Seite Live-Video): Bild der per DJI Pilot 2 (Cloud API) verbundenen
// Drohne füllt die Fläche, Fluglage als Einblendung im Bild, Karte als kleines Bild in der Ecke (Tippen tauscht
// Bild und Karte), Bedienleiste rechts in Daumenreichweite (Tablet quer).
// Funktioniert heute: Livebild startet beim Öffnen automatisch (einmal je Drohne und Besuch; hat der Pilot es in
// Pilot 2 beendet, wird es nicht erneut gestartet), Linse umschalten (live_lens_change), Telemetrie, Flugweg, HMS.
// Kamerabefehle (Zoom, Foto, Video, Gimbal, Look-At, IR-Punkt-/Flächenmessung) gehen über die Cloud-Steuerung (DRC)
// und sind nur nach Freigabe am Controller möglich (sonst gesperrt). FlyTo: Zielpunkt auf der großen Karte
// antippen, Zielhöhe (relativ zum Startpunkt) und Tempo bestätigen; dji-sync rechnet auf Ellipsoidhöhe um (DJI erwartet WGS84).
// Während des Flugs zeigt das Cockpit Restdistanz/-zeit und einen „FlyTo abbrechen“-Knopf. POI (Kreisen um einen Kartenpunkt,
// Radius = Abstand beim Start, Tempo änderbar) und Rückkehr (mit Bestätigung, abbrechbar) laufen nach demselben Muster.
// NOTSTOPP: großer roter Knopf im Bild, sichtbar sobald die Steuerung erteilt ist (auch während FlyTo/POI/Rückkehr), ohne Rückfrage;
// zusätzlich Leertaste und Gamepad B/Kreis. HANDSTEUERUNG (virtuelle Sticks, components/cockpit/): erst nach „Handsteuerung ein“,
// nur bei erteilter Steuerung + DRC + Drohne in der Luft + ohne FlyTo/POI/Rückkehr; Eingabe per Gamepad (Modus 2), Tastatur
// (W/S/A/D/Q/E/C/Z) oder Wisch-Sticks im Bild, Stufen 1/3/5 m/s, Totmann (gesendet wird nur Auslenkung, sonst 0) und
// Selbstabschaltung bei Seitenwechsel, Tab-/Fensterwechsel, Verbindungsfehler und Gamepad-Trennung. Bildverzögerung steht im OSD.

interface LiveDaten {
  lat?: number; lon?: number; hoehe_m?: number; hoehe_abs_m?: number; speed_h?: number; speed_v?: number; kurs?: number
  home_distanz_m?: number; akku_prozent?: number; restflugzeit_s?: number; rth_akku_prozent?: number
  gps_satelliten?: number; rtk_satelliten?: number; modus?: string; mode_code?: number; funk_qualitaet?: number; wind_geschaetzt_ms?: number | null; wind_richtung_grad?: number | null
  akkus?: { sn: string; prozent: number; ladezyklen: number; temperatur?: number }[]
}

interface IrPunkt { x?: number; y?: number; c: number }

interface FlyToZustand {
  id: string
  status: 'angefordert' | 'wayline_progress' | 'wayline_ok' | 'wayline_failed' | 'wayline_cancel' | null
  laeuft: boolean
  alter_s: number
  text?: string
  result?: number | null
  rest_m?: number | null
  rest_s?: number | null
  ziel?: { lat: number; lon: number; hoehe_rel: number | null } | null
  geschwindigkeit?: number | null
  entfernung_m?: number | null
}

interface PoiZustand {
  status: 'angefordert' | 'in_progress' | 'ok' | 'failed' | null
  laeuft: boolean
  alter_s: number
  text?: string
  mitte?: { lat: number; lon: number; hoehe_rel: number | null } | null
  radius_m?: number | null
  tempo?: number | null        // m/s, negativ im Uhrzeigersinn
  tempo_max?: number | null
}

interface LiveDrohne {
  sn: string
  in_der_luft: boolean
  alter_s: number
  daten: LiveDaten
  drohne: string
  kennzeichen: string | null
  fernsteuerung: string | null
  flug: { id: number; flug_nr: string | null; startzeit: string | null; pilot: string | null; co_pilot: string | null } | null
  livebild?: LivebildInfo
  hms?: { code: string; stufe: number; meldung: string | null }[]
  /** Cloud-Steuerung (DRC): Freigabe durch den Piloten, DRC-Kanal, Kamera-/Gimbal-Werte aus dem DRC-Kanal */
  steuerung?: {
    status: 'frei' | 'angefragt' | 'erteilt' | 'abgelehnt'
    nutzer_id: number | null
    nutzer: string | null
    drc_status: number
    drc_verbunden: boolean
    latenz_ms: number | null
    osd: { gimbal_pitch?: number; gimbal_yaw?: number }
    /** FlyTo (aus fly_to_point_progress): laufend oder kurz nach dem Ende mit Ergebnis */
    flyto?: FlyToZustand | null
    poi?: PoiZustand | null
    /** Handsteuerung (Sticks) läuft serverseitig */
    hand?: { aktiv: boolean; stufe: number }
    rueckkehr?: { laeuft: boolean; text?: string; status?: string; alter_s?: number } | null
    kamera: {
      zoom_faktor?: number; ir_zoom_faktor?: number; ir_max_c?: number; ir_min_c?: number; laser_distanz_m?: number
      aufnahme?: number; aufnahme_s?: number; modus?: number
      /** IR-Messung: Modus 0 aus, 1 Punkt, 2 Fläche; Koordinaten 0..1 im IR-Bild */
      ir_modus?: number
      ir_punkt?: { x?: number; y?: number; c: number }
      ir_flaeche?: { x?: number; y?: number; b?: number; h?: number; mittel_c?: number; min?: IrPunkt | null; max?: IrPunkt | null }
    }
  }
}

const LINSEN: Record<string, string> = { wide: 'Weit', zoom: 'Zoom', ir: 'IR', normal: 'Standard' }
const linsenName = (l: string | null | undefined) => (l ? LINSEN[l] ?? l : '–')

const zahl = (v: number | undefined | null, stellen = 0) =>
  v === undefined || v === null || Number.isNaN(v) ? '–' : v.toLocaleString('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen })

/** Entfernung zweier Punkte in Metern (Haversine). */
function abstandM(a: [number, number], b: [number, number]) {
  const rad = Math.PI / 180
  const h = Math.sin((b[0] - a[0]) * rad / 2) ** 2 + Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin((b[1] - a[1]) * rad / 2) ** 2
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)))
}
const meterText = (m: number) => (m >= 1000 ? `${zahl(m / 1000, 2)} km` : `${zahl(m)} m`)
const FLYTO_HOEHE_MIN = 2
const FLYTO_HOEHE_MAX = 120
const FLYTO_TEMPO_STANDARD = 10

const dauer = (sek: number | undefined | null) => {
  if (sek === undefined || sek === null) return '–'
  return `${Math.floor(sek / 60)}:${String(Math.floor(sek % 60)).padStart(2, '0')} min`
}

const minuten = (sek: number | undefined | null) => (sek === undefined || sek === null ? '–' : `${Math.floor(sek / 60)} min`)

function droneIcon(kurs: number | undefined) {
  return L.divIcon({
    className: '',
    iconSize: [56, 56],
    iconAnchor: [28, 28],
    html: `<div style="width:56px;height:56px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.4))">${liveDrohneSvg(kurs, 56)}</div>`,
  })
}

/** Karte folgt der Drohne und passt sich an, wenn sie zwischen klein und groß wechselt. */
function KarteNachfuehren({ pos, gross }: { pos: [number, number] | null; gross: boolean }) {
  const map = useMap()
  const erstes = useRef(true)
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 220)
    return () => clearTimeout(t)
  }, [gross, map])
  useEffect(() => {
    if (!pos) return
    if (erstes.current) { map.setView(pos, 17); erstes.current = false }
    else if (!map.getBounds().pad(-0.2).contains(pos)) map.panTo(pos)
  }, [pos, map])
  return null
}

/** Knopf der Bedienleiste; gesperrte Knöpfe zeigen im Titel, warum. */
function Knopf({ children, onClick, aktiv, gesperrt, titel, gefahr }: {
  children: React.ReactNode; onClick?: () => void; aktiv?: boolean; gesperrt?: boolean; titel?: string; gefahr?: boolean
}) {
  return (
    <button type="button" onClick={onClick} disabled={gesperrt} title={titel} aria-pressed={aktiv}
      className={`flex min-h-[48px] w-full items-center justify-center gap-1.5 rounded-lg border px-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        aktiv ? 'border-primary-500 bg-primary-500 text-white'
          : gefahr ? 'border-red-500/70 text-red-300 hover:bg-red-500/10'
            : 'border-dark-border bg-dark-bg-tertiary text-dark-text hover:border-primary-500/60'}`}>
      {children}
    </button>
  )
}

/** Größe eines Elements (für die Lage des Videobildes unter den Mess-Werkzeugen). */
function useGroesse<T extends HTMLElement>() {
  const [el, ref] = useState<T | null>(null)
  const [groesse, setGroesse] = useState({ b: 0, h: 0 })
  useEffect(() => {
    if (!el) return
    const messen = () => setGroesse({ b: el.clientWidth, h: el.clientHeight })
    messen()
    const ro = new ResizeObserver(messen)
    ro.observe(el)
    return () => ro.disconnect()
  }, [el])
  return { ref, ...groesse }
}

/** Das Video wird als 16:9 angenommen und eingepasst (Ränder oben/unten bzw. seitlich); Mess-Koordinaten sind 0..1 darin. */
const BILD_VERHAELTNIS = 16 / 9
function bildRahmen(b: number, h: number) {
  if (!b || !h) return { x: 0, y: 0, b: 0, h: 0 }
  const bb = Math.min(b, h * BILD_VERHAELTNIS)
  const hh = bb / BILD_VERHAELTNIS
  return { x: (b - bb) / 2, y: (h - hh) / 2, b: bb, h: hh }
}

/** Klick auf die Karte (Look-At-Ziel wählen). */
function KarteKlick({ aktiv, onKlick }: { aktiv: boolean; onKlick: (lat: number, lon: number) => void }) {
  useMapEvents({ click: e => { if (aktiv) onKlick(e.latlng.lat, e.latlng.lng) } })
  return null
}

/** Zoom-Regler: logarithmisch, gesendet wird beim Loslassen. */
function zoomZuPos(f: number, max: number) { return Math.round(100 * Math.log(Math.max(2, f) / 2) / Math.log(max / 2)) }
function posZuZoom(p: number, max: number) { return Math.round(2 * Math.pow(max / 2, p / 100) * 10) / 10 }

/** Gimbal-Feld: gedrückt halten und ziehen neigt/dreht den Gimbal (Geschwindigkeit nach Auslenkung), Loslassen hält an. */
function GimbalFeld({ gesperrt, onBewegen }: { gesperrt: boolean; onBewegen: (nick: number, gier: number, folge: boolean) => void }) {
  const feld = useRef<HTMLDivElement | null>(null)
  const vektor = useRef({ nick: 0, gier: 0 })
  const takt = useRef<ReturnType<typeof setInterval> | null>(null)
  const gesendet = useRef(false)   // wurde in diesem Zug schon eine Bewegung gesendet (der erste Befehl wird protokolliert)
  const [punkt, setPunkt] = useState<{ x: number; y: number } | null>(null)

  const anhalten = useCallback(() => {
    if (takt.current) { clearInterval(takt.current); takt.current = null; if (gesendet.current) onBewegen(0, 0, true) }
    gesendet.current = false
    vektor.current = { nick: 0, gier: 0 }
    setPunkt(null)
  }, [onBewegen])
  useEffect(() => () => { if (takt.current) clearInterval(takt.current) }, [])
  useEffect(() => { if (gesperrt) anhalten() }, [gesperrt, anhalten])

  const lesen = (e: React.PointerEvent) => {
    const r = feld.current!.getBoundingClientRect()
    let dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2)
    let dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2)
    const l = Math.hypot(dx, dy)
    if (l > 1) { dx /= l; dy /= l }
    setPunkt({ x: dx, y: dy })
    const tot = Math.hypot(dx, dy) < 0.12
    vektor.current = tot ? { nick: 0, gier: 0 } : { nick: -dy, gier: dx }
  }
  const start = (e: React.PointerEvent) => {
    if (gesperrt) return
    e.currentTarget.setPointerCapture(e.pointerId)
    lesen(e)
    gesendet.current = false
    takt.current = setInterval(() => {
      const { nick, gier } = vektor.current
      if (!gesendet.current && nick === 0 && gier === 0) return
      onBewegen(nick, gier, gesendet.current)
      gesendet.current = true
    }, 250)
  }
  return (
    <div ref={feld} role="application" aria-label="Gimbal ziehen" onPointerDown={start}
      onPointerMove={e => { if (takt.current) lesen(e) }} onPointerUp={anhalten} onPointerCancel={anhalten} onLostPointerCapture={anhalten}
      style={{ touchAction: 'none' }}
      className={`relative mx-auto h-36 w-36 select-none rounded-full border-2 ${gesperrt ? 'border-white/10 opacity-40' : 'border-primary-500/70'} bg-black/30`}>
      <span className="absolute left-1/2 top-1 -translate-x-1/2 text-[10px] text-white/50">hoch</span>
      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] text-white/50">runter</span>
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-white/50">links</span>
      <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-white/50">rechts</span>
      <span className="pointer-events-none absolute h-12 w-12 rounded-full bg-primary-500/80 shadow"
        style={{ left: `calc(50% + ${(punkt?.x ?? 0) * 42}px - 24px)`, top: `calc(50% + ${(punkt?.y ?? 0) * 42}px - 24px)` }} />
    </div>
  )
}

const Ueberschrift = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wider text-dark-text-subtle">{children}</div>
)

const OHNE_STEUERUNG = 'Erst „Steuerung anfragen“ – der Pilot muss am Controller zustimmen.'

type Werkzeug = null | 'lookat' | 'ir_punkt' | 'ir_flaeche'
const ZOOM_PRESETS: Record<string, number[]> = { zoom: [2, 5, 10, 25], ir: [2, 5, 10, 20] }
const ZOOM_MAX: Record<string, number> = { zoom: 200, ir: 20 }

export function CoPilotCockpitPage() {
  const [drohnen, setDrohnen] = useState<LiveDrohne[]>([])
  const [auswahl, setAuswahl] = useState('')
  const [geladen, setGeladen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [aktionLaeuft, setAktionLaeuft] = useState(false)
  const [aktionFehler, setAktionFehler] = useState<string | null>(null)
  const [karteGross, setKarteGross] = useState(false)
  const [flugweg, setFlugweg] = useState<[number, number][]>([])
  const [werkzeug, setWerkzeug] = useState<Werkzeug>(null)
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null)
  const [befehlLaeuft, setBefehlLaeuft] = useState(false)
  const [lookAtZiel, setLookAtZiel] = useState<[number, number] | null>(null)
  const [irZiel, setIrZiel] = useState<{ x: number; y: number; b?: number; h?: number } | null>(null)
  const [ziehen, setZiehen] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null)
  const [zoomPos, setZoomPos] = useState<number | null>(null)   // Reglerstellung, solange gezogen wird
  // FlyTo: Kartenmodus „Ziel wählen“, gewählter Punkt, Eingaben des Dialogs
  const [punktWahl, setPunktWahl] = useState<null | 'flyto' | 'poi'>(null)
  const [poiHoehe, setPoiHoehe] = useState('0')
  const [poiTempo, setPoiTempo] = useState<number | null>(null)   // Reglerstellung, solange gezogen wird
  const [rthDialog, setRthDialog] = useState(false)
  const [telemetrieOffen, setTelemetrieOffen] = useState(false)
  const [rthSendet, setRthSendet] = useState(false)
  const [flytoZiel, setFlytoZiel] = useState<[number, number] | null>(null)
  const [flytoHoehe, setFlytoHoehe] = useState('')
  const [flytoTempo, setFlytoTempo] = useState(FLYTO_TEMPO_STANDARD)
  const [flytoSendet, setFlytoSendet] = useState(false)
  const [flytoFehler, setFlytoFehler] = useState<string | null>(null)
  const flytoGemeldet = useRef('')
  const flytoWahl = punktWahl === 'flyto'
  const poiWahl = punktWahl === 'poi'
  const meldungTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bild = useGroesse<HTMLDivElement>()
  const [touchSticks, setTouchSticks] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches)
  const [notstoppSendet, setNotstoppSendet] = useState(false)
  /** Bildübertragung (vom Livebild-Player gemeldet): Weg und Verzögerung; null = unbekannt */
  const [uebertragung, setUebertragung] = useState<UebertragungsInfo | null>(null)

  const laden = () => api.get('/dji/live')
    .then(r => { setDrohnen(r.data as LiveDrohne[]); setFehler(null) })
    .catch(() => setFehler('Live-Daten konnten nicht geladen werden.'))
    .finally(() => setGeladen(true))

  useEffect(() => {
    laden()
    const t = setInterval(laden, 2000)
    return () => clearInterval(t)
  }, [])
  useConnectionRetry(() => { laden() })

  // Drohne in der Luft bevorzugen; Auswahl bleibt, solange die Drohne gemeldet wird
  useEffect(() => {
    if (auswahl && drohnen.some(d => d.sn === auswahl)) return
    const erste = drohnen.find(d => d.in_der_luft) ?? drohnen[0]
    setAuswahl(erste?.sn ?? '')
  }, [drohnen, auswahl])

  const drohne = drohnen.find(d => d.sn === auswahl) ?? null

  // Flugweg des laufenden Fluges für die Karte (alle 10 s)
  useEffect(() => {
    if (!drohne?.in_der_luft) { setFlugweg([]); return }
    let aktiv = true
    const holen = () => api.get(`/dji/live/${encodeURIComponent(drohne.sn)}/flugweg`)
      .then(r => aktiv && setFlugweg((r.data as [number, number, number][]).map(p => [p[0], p[1]])))
      .catch(() => { /* letzter Flugweg bleibt stehen; Netzwerk/5xx meldet der Interceptor (Statusleiste) */ })
    holen()
    const t = setInterval(holen, 10000)
    return () => { aktiv = false; clearInterval(t) }
  }, [drohne?.sn, drohne?.in_der_luft, drohne?.flug?.id])

  // Ende eines FlyTo (Ziel erreicht / abgebrochen / Fehler) einmal als Meldung zeigen
  const flytoStand = drohne?.steuerung?.flyto
  useEffect(() => {
    if (!flytoStand || flytoStand.laeuft || !flytoStand.status) return
    const schluessel = `${flytoStand.id}:${flytoStand.status}`
    if (flytoGemeldet.current === schluessel) return
    flytoGemeldet.current = schluessel
    melden(`FlyTo: ${flytoStand.text || 'beendet'}`, flytoStand.status === 'wayline_failed')
  }, [flytoStand?.id, flytoStand?.status, flytoStand?.laeuft])

  const lb = drohne?.livebild
  const aktiveKamera = (lb?.kameras ?? []).find(k => k.aktiv) ?? null
  const linsen = useMemo(() => {
    const gemeldet = aktiveKamera?.linsen ?? []
    return gemeldet.length ? gemeldet : ['wide', 'zoom', 'ir']
  }, [aktiveKamera])
  const linse = lb?.linse ?? aktiveKamera?.linse
  const streamAktiv = !!(lb?.laeuft || lb?.dji_meldet_stream)

  async function steuern(pfad: string) {
    if (!drohne) return
    setAktionLaeuft(true); setAktionFehler(null)
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne.sn)}/livebild/${pfad}`)
      laden()
    } catch (e: any) {
      setAktionFehler(e?.response?.data?.detail || 'Aktion fehlgeschlagen')
    } finally { setAktionLaeuft(false) }
  }

  // Livebild automatisch anfordern, sobald die Fernsteuerung Kameras meldet und noch nichts überträgt.
  // Einmal je Drohne und Seitenbesuch; meldet die Fernsteuerung keine Kameras mehr (getrennt), gilt es als neu.
  const autoGestartet = useRef<Map<string, number>>(new Map())   // SN -> Zeitpunkt der Anforderung
  const [autoFehler, setAutoFehler] = useState<string | null>(null)
  useEffect(() => {
    if (!drohne) return
    if (!lb?.anforderbar) { autoGestartet.current.delete(drohne.sn); return }
    if (streamAktiv || autoGestartet.current.has(drohne.sn)) return
    autoGestartet.current.set(drohne.sn, Date.now())
    setAutoFehler(null)
    api.post(`/dji/live/${encodeURIComponent(drohne.sn)}/livebild/start`)
      .then(() => laden())
      .catch((e: any) => setAutoFehler(e?.response?.data?.detail || 'Live-Video konnte nicht gestartet werden'))
  }, [drohne?.sn, lb?.anforderbar, streamAktiv])
  // Bis 20 s nach der Anforderung gilt das Bild als „wird angefordert“, danach als beendet (z. B. vom Piloten in Pilot 2)
  const angefordertSeit = drohne ? autoGestartet.current.get(drohne.sn) : undefined
  const wirdAngefordert = !streamAktiv && !autoFehler && (angefordertSeit === undefined || Date.now() - angefordertSeit < 20000)

  async function steuerung(pfad: 'anfragen' | 'abgeben') {
    if (!drohne) return
    setAktionLaeuft(true); setAktionFehler(null)
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne.sn)}/steuerung/${pfad}`)
      laden()
    } catch (e: any) {
      setAktionFehler(e?.response?.data?.detail || (pfad === 'anfragen' ? 'Steuerung konnte nicht angefragt werden' : 'Steuerung konnte nicht abgegeben werden'))
    } finally { setAktionLaeuft(false) }
  }

  function melden(text: string, fehler = false) {
    setMeldung({ text, fehler })
    if (meldungTimer.current) clearTimeout(meldungTimer.current)
    meldungTimer.current = setTimeout(() => setMeldung(null), fehler ? 9000 : 3500)
  }
  useEffect(() => () => { if (meldungTimer.current) clearTimeout(meldungTimer.current) }, [])

  // Kamera-/Gimbal-Befehl über die Cloud-Steuerung (dji-sync prüft Freigabe und DRC-Kanal und schreibt das Protokoll).
  // leise: Dauerbefehle (Gimbal ziehen) melden nur Fehler.
  const kameraBefehl = useCallback(async (aktion: string, daten: Record<string, unknown> = {}, leise = false): Promise<boolean> => {
    if (!auswahl) return false
    if (!leise) setBefehlLaeuft(true)
    try {
      const r = await api.post(`/dji/live/${encodeURIComponent(auswahl)}/kamera/${aktion}`, daten)
      if (!leise) melden(`${(r.data as { meldung?: string })?.meldung || 'Befehl'} – ausgeführt`)
      return true
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      melden(typeof detail === 'string' ? detail : 'Kamerabefehl fehlgeschlagen – keine Antwort vom Server', true)
      return false
    } finally { if (!leise) setBefehlLaeuft(false) }
  }, [auswahl])

  // Gimbal ziehen: Dauerbefehl aus dem Gimbal-Feld; nur eine Anfrage gleichzeitig, das Anhalten wird immer gesendet
  const gimbalLaeuft = useRef(false)
  const gimbalBewegen = useCallback(async (nick: number, gier: number, folge: boolean) => {
    const stopp = nick === 0 && gier === 0
    if (gimbalLaeuft.current && folge && !stopp) return
    gimbalLaeuft.current = true
    try { await kameraBefehl('gimbal_drehen', { nick, gier, folge }, true) } finally { gimbalLaeuft.current = false }
  }, [kameraBefehl])

  // Notstopp: Befehl an die Drohne, ohne Rückfrage (die Handsteuerung wird vorher schon abgeschaltet)
  const notstoppSenden = useCallback(async () => {
    if (!auswahl) return
    setNotstoppSendet(true)
    try {
      const r = await api.post(`/dji/live/${encodeURIComponent(auswahl)}/steuerung/notstopp`, {}, { timeout: 15000 })
      melden((r.data as { meldung?: string })?.meldung || 'NOTSTOPP ausgelöst – die Drohne hält an')
      laden()
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      melden(typeof detail === 'string' ? `NOTSTOPP fehlgeschlagen: ${detail}` : 'NOTSTOPP fehlgeschlagen – keine Antwort vom Server. Sofort am Controller eingreifen!', true)
    } finally { setNotstoppSendet(false) }
  }, [auswahl])

  // Handsteuerung: Sperrgrund wie bei den anderen Knöpfen (null = möglich)
  const stH = drohne?.steuerung
  const steuerungErteilt = stH?.status === 'erteilt'
  const handSperre: string | null = !drohne ? 'Keine Drohne verbunden.'
    : !(steuerungErteilt && stH!.drc_verbunden && drohne.alter_s <= 10) ? OHNE_STEUERUNG
      : !drohne.in_der_luft ? 'Nur möglich, solange die Drohne fliegt.'
        : stH!.rueckkehr?.laeuft ? 'Die Drohne fliegt zurück – zuerst die Rückkehr abbrechen.'
          : stH!.flyto?.laeuft ? 'Es läuft ein FlyTo – zuerst abbrechen.' : stH!.poi?.laeuft ? 'Es läuft ein POI – zuerst beenden.' : null
  const hand = useHandsteuerung({ sn: auswahl, sperre: handSperre, steuerungErteilt, onNotstopp: notstoppSenden, onMeldung: melden })
  const handAus = hand.ausschalten
  useEffect(() => { handAus() }, [auswahl, handAus])   // andere Drohne gewählt: Handsteuerung aus

  if (!geladen) return <div className="p-6 text-sm text-dark-text-muted">Lade Live-Daten …</div>

  if (!drohne) {
    return (
      <KeineDrohneLeer fehler={fehler} />
    )
  }

  const d = drohne.daten || {}
  const pos: [number, number] | null = d.lat && d.lon ? [d.lat, d.lon] : null
  const veraltet = drohne.alter_s > 10
  const akkuKritisch = (d.akku_prozent ?? 100) <= (d.rth_akku_prozent ?? 25) + 5
  const hms = drohne.hms ?? []
  const flugzeit = drohne.flug?.startzeit ? (Date.now() - new Date(drohne.flug.startzeit).getTime()) / 1000 : null
  const st = drohne.steuerung
  // Kamera-/Gimbal-Werte kommen nur über den DRC-Kanal; ohne Steuerung wären sie veraltet
  const drcAktiv = st?.status === 'erteilt' && st.drc_verbunden
  const kam = drcAktiv ? st!.kamera : {}
  const bereit = drcAktiv && !veraltet
  const kameraTitel = bereit ? undefined : OHNE_STEUERUNG
  const zoomLinse = linse === 'zoom' || linse === 'ir' ? linse : null
  const zoomMax = zoomLinse ? ZOOM_MAX[zoomLinse] : 200
  const zoomIst = zoomLinse === 'ir' ? kam.ir_zoom_faktor : kam.zoom_faktor
  const zoomAnzeige = zoomPos !== null ? posZuZoom(zoomPos, zoomMax) : zoomIst
  const irLinse = linse === 'ir'
  const aufnahme = kam.aufnahme === 1
  const bildR = bildRahmen(bild.b, bild.h)
  const steuerText = !st || st.status === 'frei' ? 'Pilot steuert allein'
    : st.status === 'angefragt' ? 'Wartet auf Zustimmung am Controller …'
      : st.status === 'abgelehnt' ? 'Pilot hat die Anfrage abgelehnt'
        : `Freigegeben an ${st.nutzer || '–'} · ${st.drc_verbunden ? `verbunden${st.latenz_ms ? ` (${st.latenz_ms} ms)` : ''}` : st.drc_status === 1 ? 'Verbindung wird aufgebaut …' : 'Verbindung gestört'}`
  const steuerFarbe = st?.status === 'erteilt' ? (st.drc_verbunden ? 'bg-emerald-400' : 'bg-amber-400')
    : st?.status === 'angefragt' ? 'bg-amber-400 animate-pulse' : st?.status === 'abgelehnt' ? 'bg-red-500' : 'bg-slate-500'

  // Look-At: angetippter Kartenpunkt; Zielhöhe = Höhe des Startpunkts (Ellipsoidhöhe der Drohne minus Höhe über Start)
  async function lookAt(lat: number, lon: number) {
    if (!bereit) { melden(OHNE_STEUERUNG, true); return }
    if (d.hoehe_abs_m === undefined || d.hoehe_m === undefined || d.hoehe_abs_m === null || d.hoehe_m === null) {
      melden('Look-At nicht möglich: Die Drohne meldet keine Höhe.', true); return
    }
    setLookAtZiel([lat, lon])
    if (await kameraBefehl('look_at', { lat, lon, hoehe: Math.max(2, d.hoehe_abs_m - d.hoehe_m) })) setWerkzeug(null)
  }

  // FlyTo: Kartenmodus starten (Karte groß, Tippen setzt das Ziel), Dialog, Start und Abbruch
  const flytoLaeuft = !!st?.flyto?.laeuft
  const poiLaeuft = !!st?.poi?.laeuft
  const rthLaeuft = !!st?.rueckkehr?.laeuft
  const flugGesperrtTitel = !drohne.in_der_luft ? 'Nur möglich, solange die Drohne fliegt.' : !bereit ? OHNE_STEUERUNG
    : rthLaeuft ? 'Die Drohne fliegt zurück – zuerst die Rückkehr abbrechen.'
      : flytoLaeuft ? 'Es läuft ein FlyTo – zuerst abbrechen.' : poiLaeuft ? 'Es läuft ein POI – zuerst beenden.' : undefined
  // Der Server lehnt FlyTo/POI ab, solange die Handsteuerung läuft (Rückkehr beendet sie dagegen)
  const flyPoiGesperrtTitel = flugGesperrtTitel ?? (hand.an || st?.hand?.aktiv ? 'Handsteuerung ist an – zuerst ausschalten.' : undefined)
  function flytoStarten() {
    setWerkzeug(null); setKarteGross(true); setRthDialog(false); setPunktWahl('flyto'); setFlytoZiel(null); setFlytoFehler(null)
  }
  function poiStarten() {
    setWerkzeug(null); setKarteGross(true); setRthDialog(false); setPunktWahl('poi'); setFlytoZiel(null); setFlytoFehler(null)
  }
  function flytoVerwerfen() { setPunktWahl(null); setFlytoZiel(null); setFlytoFehler(null) }
  function flytoZielSetzen(lat: number, lon: number) {
    setFlytoZiel([lat, lon]); setFlytoFehler(null)
    // Vorbelegung: aktuelle Höhe über dem Startpunkt (innerhalb der erlaubten Grenzen)
    setFlytoHoehe(h => h || String(Math.min(FLYTO_HOEHE_MAX, Math.max(FLYTO_HOEHE_MIN, Math.round(d.hoehe_m ?? 30)))))
  }
  const flytoHoeheZahl = Number(flytoHoehe.replace(',', '.'))
  const flytoHoeheOk = flytoHoehe.trim() !== '' && Number.isFinite(flytoHoeheZahl) && flytoHoeheZahl >= FLYTO_HOEHE_MIN && flytoHoeheZahl <= FLYTO_HOEHE_MAX
  const flytoEntfernung = flytoZiel && pos ? abstandM(pos, flytoZiel) : null
  async function flytoAbsenden() {
    if (!flytoZiel || !flytoHoeheOk) return
    setFlytoSendet(true); setFlytoFehler(null)
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne!.sn)}/flyto/start`,
        { lat: flytoZiel[0], lon: flytoZiel[1], hoehe: flytoHoeheZahl, geschwindigkeit: flytoTempo }, { timeout: 25000 })
      flytoVerwerfen(); setKarteGross(true); laden()
      melden('FlyTo gestartet – die Drohne fliegt zum Ziel')
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      setFlytoFehler(typeof detail === 'string' ? detail : Array.isArray(detail) ? 'Eingabe ungültig – bitte Werte prüfen' : 'FlyTo konnte nicht gestartet werden – keine Antwort vom Server')
    } finally { setFlytoSendet(false) }
  }
  async function flytoAbbrechen() {
    setFlytoSendet(true)
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne!.sn)}/flyto/stop`, {}, { timeout: 25000 })
      melden('FlyTo abgebrochen – die Drohne hält an')
      laden()
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      melden(typeof detail === 'string' ? detail : 'FlyTo konnte nicht abgebrochen werden – bitte erneut versuchen oder am Controller eingreifen', true)
    } finally { setFlytoSendet(false) }
  }
  // POI: Mittelpunkt = gewählter Kartenpunkt, Höhe relativ zum Startpunkt; Radius ergibt sich aus dem Abstand der Drohne
  const poiHoeheZahl = Number(poiHoehe.replace(',', '.'))
  const poiHoeheOk = poiHoehe.trim() !== '' && Number.isFinite(poiHoeheZahl) && poiHoeheZahl >= 0 && poiHoeheZahl <= 120
  async function poiAbsenden() {
    if (!flytoZiel || !poiHoeheOk) return
    setFlytoSendet(true); setFlytoFehler(null)
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne!.sn)}/poi/start`, { lat: flytoZiel[0], lon: flytoZiel[1], hoehe: poiHoeheZahl }, { timeout: 25000 })
      flytoVerwerfen(); laden()
      melden('POI gestartet – die Drohne kreist um den Punkt')
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      setFlytoFehler(typeof detail === 'string' ? detail : Array.isArray(detail) ? 'Eingabe ungültig – bitte Werte prüfen' : 'POI konnte nicht gestartet werden – keine Antwort vom Server')
    } finally { setFlytoSendet(false) }
  }
  async function poiBeenden() {
    setFlytoSendet(true)
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne!.sn)}/poi/stop`, {}, { timeout: 25000 })
      melden('POI beendet'); laden()
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      melden(typeof detail === 'string' ? detail : 'POI konnte nicht beendet werden – bitte erneut versuchen oder am Controller eingreifen', true)
    } finally { setFlytoSendet(false) }
  }
  async function poiTempoSenden(v: number) {
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne!.sn)}/poi/tempo`, { tempo: v }, { timeout: 25000 })
      melden(`Kreisgeschwindigkeit ${zahl(Math.abs(v), 1)} m/s ${v < 0 ? 'im Uhrzeigersinn' : v > 0 ? 'gegen den Uhrzeigersinn' : ''}`)
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      melden(typeof detail === 'string' ? detail : 'Geschwindigkeit konnte nicht geändert werden', true)
    } finally { setPoiTempo(null); laden() }
  }
  async function rthAusloesen() {
    setRthSendet(true)
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne!.sn)}/rueckkehr/start`, {}, { timeout: 35000 })
      setRthDialog(false); melden('Rückkehr gestartet – die Drohne fliegt zum Startpunkt zurück'); laden()
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      melden(typeof detail === 'string' ? detail : 'Rückkehr konnte nicht gestartet werden – bitte am Controller eingreifen', true)
      setRthDialog(false)
    } finally { setRthSendet(false) }
  }
  async function rthAbbrechen() {
    setRthSendet(true)
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne!.sn)}/rueckkehr/abbrechen`, {}, { timeout: 25000 })
      melden('Rückkehr abgebrochen – die Drohne schwebt'); laden()
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      melden(typeof detail === 'string' ? detail : 'Rückkehr konnte nicht abgebrochen werden – bitte am Controller eingreifen', true)
    } finally { setRthSendet(false) }
  }
  const pz = st?.poi
  const poiMitte: [number, number] | null = pz?.laeuft && pz.mitte ? [pz.mitte.lat, pz.mitte.lon] : null
  const fz = st?.flyto
  const flytoZielAktiv: [number, number] | null = fz?.laeuft && fz.ziel ? [fz.ziel.lat, fz.ziel.lon] : null

  // IR-Messung: Punkt antippen bzw. Rahmen ziehen (Koordinaten 0..1 im Videobild)
  const bildPunkt = (e: React.PointerEvent) => {
    const r = e.currentTarget.getBoundingClientRect()
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) }
  }
  const irDown = (e: React.PointerEvent) => {
    if (werkzeug !== 'ir_punkt' && werkzeug !== 'ir_flaeche') return
    e.currentTarget.setPointerCapture(e.pointerId)
    const p = bildPunkt(e)
    setZiehen({ x0: p.x, y0: p.y, x1: p.x, y1: p.y })
  }
  const irMove = (e: React.PointerEvent) => { if (ziehen) { const p = bildPunkt(e); setZiehen({ ...ziehen, x1: p.x, y1: p.y }) } }
  const irUp = async (e: React.PointerEvent) => {
    if (!ziehen) return
    const p = bildPunkt(e)
    const z = { ...ziehen, x1: p.x, y1: p.y }
    setZiehen(null)
    if (werkzeug === 'ir_punkt') {
      setIrZiel({ x: z.x0, y: z.y0 })
      if (await kameraBefehl('ir_punkt', { x: z.x0, y: z.y0 })) setWerkzeug(null)
    } else if (werkzeug === 'ir_flaeche') {
      let x = Math.min(z.x0, z.x1), y = Math.min(z.y0, z.y1)
      let b = Math.abs(z.x1 - z.x0), h = Math.abs(z.y1 - z.y0)
      if (b < 0.03 || h < 0.03) {   // nur getippt: Standardfläche um den Punkt
        b = 0.2; h = 0.2
        x = Math.min(0.8, Math.max(0, z.x0 - 0.1)); y = Math.min(0.8, Math.max(0, z.y0 - 0.1))
      }
      setIrZiel({ x, y, b, h })
      if (await kameraBefehl('ir_flaeche', { x, y, breite: b, hoehe: h })) setWerkzeug(null)
    }
  }

  // Messwerte der IR-Messung (vom Gerät gemeldet) mit Position im Bild; gesetzte Fläche/Punkt dient als Rückfall
  const irModus = kam.ir_modus ?? (irZiel ? (irZiel.b ? 2 : 1) : 0)
  const pct = (v: number | undefined) => `${(v ?? 0) * 100}%`
  const marke = (p: IrPunkt | null | undefined, farbe: string, name: string) => p && p.x !== undefined && p.y !== undefined ? (
    <div key={name} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: pct(p.x), top: pct(p.y) }}>
      <span className={`block h-3.5 w-3.5 rounded-full border-2 border-white ${farbe}`} />
      <span className="absolute left-4 top-0 whitespace-nowrap rounded bg-black/75 px-1.5 py-0.5 font-mono text-xs font-bold">{zahl(p.c, 1)} °C</span>
    </div>
  ) : null
  const irMessung = irModus === 1 ? (() => {
    const p = kam.ir_punkt && kam.ir_punkt.x !== undefined ? kam.ir_punkt : irZiel ? { x: irZiel.x, y: irZiel.y, c: kam.ir_punkt?.c } : null
    return p ? (
      <div className="pointer-events-none absolute inset-0">
        {p.c !== undefined && p.c !== null ? marke(p as IrPunkt, 'bg-amber-400', 'punkt') : (
          <div className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-amber-300" style={{ left: pct(p.x), top: pct(p.y) }} />)}
      </div>
    ) : null
  })() : irModus === 2 ? (() => {
    const f = kam.ir_flaeche
    const r = f && f.x !== undefined ? f : irZiel?.b ? { x: irZiel.x, y: irZiel.y, b: irZiel.b, h: irZiel.h } : null
    return r ? (
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute border-2 border-amber-300" style={{ left: pct(r.x), top: pct(r.y), width: pct(r.b), height: pct(r.h) }} />
        {marke(f?.max, 'bg-red-500', 'max')}
        {marke(f?.min, 'bg-sky-400', 'min')}
      </div>
    ) : null
  })() : null
  const irFl = kam.ir_flaeche
  const irPos = (p: IrPunkt | null | undefined) => p && p.x !== undefined && p.y !== undefined ? ` bei ${zahl(p.x * 100)} % / ${zahl((p.y ?? 0) * 100)} %` : ''
  const irText = irModus === 1 ? (
    <div className="rounded bg-black/55 px-2 py-1">
      IR-Punkt {kam.ir_punkt ? `${zahl(kam.ir_punkt.c, 1)} °C${irPos(kam.ir_punkt)}` : 'wird gemessen …'}
    </div>
  ) : irModus === 2 ? (
    <div className="space-y-0.5 rounded bg-black/55 px-2 py-1">
      <div className="font-sans font-semibold text-amber-200">IR-Fläche</div>
      {irFl && (irFl.max || irFl.min || irFl.mittel_c !== undefined) ? (
        <>
          {irFl.max && <div><span className="text-red-300">Max</span> {zahl(irFl.max.c, 1)} °C{irPos(irFl.max)}</div>}
          {irFl.min && <div><span className="text-sky-300">Min</span> {zahl(irFl.min.c, 1)} °C{irPos(irFl.min)}</div>}
          {irFl.mittel_c !== undefined && <div className="text-white/70">Ø {zahl(irFl.mittel_c, 1)} °C</div>}
        </>
      ) : <div className="text-white/70">Messfläche gesetzt – Messwerte folgen …</div>}
    </div>
  ) : null

  // Bild und Karte bleiben beide eingehängt (kein Neuladen des Players), nur Größe und Lage wechseln
  const gross = 'absolute inset-0 z-0 bg-black'
  const klein = `absolute ${hand.an && touchSticks ? 'bottom-[190px]' : 'bottom-3'} right-3 z-20 bg-black aspect-[4/3] w-[32%] max-w-[300px] min-w-[150px] overflow-hidden rounded-lg border border-white/20 shadow-lg`

  return (
    <div className="-m-2 flex ui-fill min-h-[540px] flex-col overflow-hidden rounded-xl border border-dark-border bg-[#0e151c] text-[#e4ebf1] sm:-m-3">
      {/* Statusleiste */}
      <div className="flex min-h-[44px] flex-wrap items-center gap-x-4 gap-y-1 border-b border-white/10 bg-[#17222d] px-3 py-1.5 font-mono text-xs">
        <span className="flex items-center gap-1.5">
          <span className={`h-2.5 w-2.5 rounded-full ${veraltet ? 'bg-red-500' : drohne.in_der_luft ? 'bg-emerald-400' : 'bg-slate-400'}`} />
          {drohnen.length > 1 ? (
            <Select value={auswahl} onChange={e => setAuswahl(e.target.value)} className="!min-h-[48px] text-xs"
              options={drohnen.map(x => ({ value: x.sn, label: `${x.kennzeichen || x.drohne}${x.in_der_luft ? ' · in der Luft' : ''}` }))} />
          ) : <b className="font-sans text-sm">{drohne.kennzeichen || drohne.drohne}</b>}
        </span>
        <span className="text-white/60">{drohne.flug?.flug_nr || (drohne.in_der_luft ? 'Flug' : 'am Boden')} · Pilot {drohne.flug?.pilot || <span className="text-amber-300">offen</span>}</span>
        <span className="flex-1" />
        <span className="text-white/60">{d.modus || '–'}</span>
        <span className="text-xl font-bold leading-none">Funk {d.funk_qualitaet !== undefined && d.funk_qualitaet !== null ? `${d.funk_qualitaet}/5` : '–'}</span>
        <span className={`text-xl font-bold leading-none ${akkuKritisch ? 'text-red-300' : ''}`}>Akku {zahl(d.akku_prozent)} %</span>
        {veraltet && <span className="font-bold text-red-300">keine Daten seit {Math.round(drohne.alter_s)} s</span>}
        {st?.status === 'erteilt' && (
          <span className={`rounded px-2 py-0.5 font-sans font-semibold ${st.drc_verbunden ? 'bg-primary-500 text-white' : 'bg-amber-500 text-black'}`}>
            Cloud-Steuerung: {st.nutzer || '–'}
          </span>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* Bild mit Einblendungen + Karte als Bild-im-Bild */}
        <div ref={bild.ref} className="relative min-h-[260px] flex-1 overflow-hidden bg-black">
          <div className={karteGross ? klein : gross} onClick={karteGross ? () => setKarteGross(false) : undefined}>
            {lb?.url ? (
              <LivebildPlayer url={lb.url} titel={`Live-Video ${drohne.kennzeichen || drohne.drohne}`}
                pointerEventsNone={karteGross} onUebertragung={setUebertragung} />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-white/60">
                <VideoOff className="h-8 w-8" />
                {karteGross ? 'Kein Bild' : lb?.dji_meldet_stream ? 'Die Drohne sendet – das Bild wird gleich angezeigt …'
                  : !lb?.anforderbar ? 'Kein Live-Video: Livestream nicht freigegeben. Auf der Fernbedienung die Cloud-Seite (Open Platform) öffnen.'
                    : autoFehler ? 'Live-Video konnte nicht gestartet werden.' : wirdAngefordert ? 'Live-Video wird angefordert …'
                      : 'Live-Video beendet (z. B. in DJI Pilot 2 auf der AirLink-Seite).'}
              </div>
            )}
            {karteGross && <span className="absolute left-1.5 top-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px]">Bild · tippen zum Tauschen</span>}
          </div>

          <div className={karteGross ? gross : klein}>
            <MapContainer center={pos || [50.1109, 8.6821]} zoom={17} className="h-full w-full" zoomControl={karteGross}
              attributionControl={karteGross} dragging={karteGross} scrollWheelZoom={karteGross} doubleClickZoom={karteGross} touchZoom={karteGross}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
              {flugweg.length > 1 && <Polyline positions={flugweg} pathOptions={{ color: '#d9282f', weight: 3 }} />}
              {pos && <Marker position={pos} icon={droneIcon(d.kurs)} />}
              {flytoZielAktiv && pos && <Polyline positions={[pos, flytoZielAktiv]} pathOptions={{ color: '#38bdf8', weight: 3, dashArray: '6 8' }} />}
              {flytoZielAktiv && <CircleMarker center={flytoZielAktiv} radius={10} pathOptions={{ color: '#38bdf8', fillColor: '#38bdf8', fillOpacity: 0.35, weight: 3 }} />}
              {punktWahl && flytoZiel && <CircleMarker center={flytoZiel} radius={11} pathOptions={{ color: '#f97316', fillColor: '#f97316', fillOpacity: 0.45, weight: 3 }} />}
              {poiMitte && pos && <CircleMarker center={poiMitte} radius={10} pathOptions={{ color: '#a855f7', fillColor: '#a855f7', fillOpacity: 0.4, weight: 3 }} />}
              {lookAtZiel && <CircleMarker center={lookAtZiel} radius={9} pathOptions={{ color: '#facc15', fillColor: '#facc15', fillOpacity: 0.35, weight: 3 }} />}
              <KarteNachfuehren pos={pos} gross={karteGross} />
              <KarteKlick aktiv={werkzeug === 'lookat' || punktWahl !== null} onKlick={(la, lo) => (punktWahl ? flytoZielSetzen(la, lo) : lookAt(la, lo))} />
            </MapContainer>
            {!karteGross && werkzeug === 'lookat' && (
              <span className="pointer-events-none absolute inset-x-0 top-0 z-20 bg-amber-400/90 px-1.5 py-0.5 text-center text-[10px] font-semibold text-black">Ziel auf der Karte antippen</span>
            )}
            {!karteGross && werkzeug !== 'lookat' && (
              <button type="button" onClick={() => setKarteGross(true)} aria-label="Karte groß anzeigen"
                className="absolute inset-0 z-20 flex items-start justify-start bg-transparent p-1.5">
                <span className="rounded bg-black/60 px-1.5 py-0.5 text-[10px]">Karte · tippen zum Tauschen</span>
              </button>
            )}
            {!pos && <div className="absolute inset-x-0 bottom-0 z-10 bg-black/60 px-2 py-1 text-center text-[10px] text-amber-300">keine Position (kein GPS-Fix)</div>}
          </div>

          {/* Meldung zum letzten Kamerabefehl (auch Fehlerantworten der Drohne) */}
          {meldung && (
            <div role="status" className={`pointer-events-none absolute inset-x-3 top-3 z-[38] mx-auto max-w-xl rounded-lg px-3 py-2 text-center text-sm font-semibold shadow-lg ${
              meldung.fehler ? 'bg-red-600 text-white' : 'bg-emerald-600/95 text-white'}`}>
              {meldung.text}
            </div>
          )}

          {/* FlyTo: Hinweis im Modus „Ziel wählen“ */}
          {punktWahl && !flytoZiel && (
            <div className="absolute inset-x-3 top-3 z-30 mx-auto flex max-w-xl items-center justify-between gap-3 rounded-lg bg-orange-500 px-3 py-2 text-sm font-semibold text-black shadow-lg">
              <span>{poiWahl ? 'POI: Mittelpunkt auf der Karte antippen' : 'FlyTo: Ziel auf der Karte antippen'}</span>
              <button type="button" onClick={flytoVerwerfen} className="min-h-[48px] rounded border border-black/40 px-3">Abbrechen</button>
            </div>
          )}

          {/* FlyTo: Dialog mit Zielhöhe, Tempo und Entfernung (zugleich die Bestätigung) */}
          {poiWahl && flytoZiel && (
            <div role="dialog" aria-label="POI bestätigen" className="fixed inset-x-3 bottom-3 z-[1000] mx-auto max-h-[88dvh] max-w-md space-y-3 overflow-y-auto md:absolute md:z-30 rounded-xl border border-purple-400/60 bg-[#0e151c] p-3.5 text-sm shadow-xl">
              <div className="flex items-center justify-between">
                <b className="font-sans text-base">POI: Kreisen um den markierten Punkt</b>
                <span className="font-mono text-xs text-white/60">{flytoZiel[0].toFixed(5)}, {flytoZiel[1].toFixed(5)}</span>
              </div>
              <div className="text-white/80">Kreisradius = aktueller Abstand der Drohne: <b className="tabular-nums">{flytoEntfernung !== null ? meterText(flytoEntfernung) : '–'}</b></div>
              {flytoEntfernung !== null && flytoEntfernung < 5 && <p className="text-xs text-amber-200">Sehr kleiner Radius – die Drohne ist fast über dem Mittelpunkt.</p>}
              <label className="block space-y-1">
                <span className="text-white/70">Höhe des Mittelpunkts über dem Startpunkt (m, 0–120) – z. B. Dachhöhe des Objekts</span>
                <input type="number" inputMode="decimal" min={0} max={120} step={1} value={poiHoehe} onChange={e => setPoiHoehe(e.target.value)}
                  className="min-h-[48px] w-full rounded-lg border border-white/20 bg-black/40 px-3 text-base tabular-nums" />
              </label>
              {!poiHoeheOk && <p className="text-xs text-red-300">Höhe muss zwischen 0 und 120 m liegen.</p>}
              <p className="text-xs text-white/60">Tempo und Drehrichtung lassen sich nach dem Start einstellen. Der Pilot kann jederzeit am Controller eingreifen.</p>
              {flytoFehler && <p role="alert" className="rounded-lg bg-red-600 px-2.5 py-2 text-sm font-semibold text-white">{flytoFehler}</p>}
              <div className="grid grid-cols-2 gap-2">
                <Knopf onClick={flytoVerwerfen} gesperrt={flytoSendet}>Verwerfen</Knopf>
                <button type="button" onClick={poiAbsenden} disabled={flytoSendet || !poiHoeheOk || !bereit || !drohne.in_der_luft}
                  className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-lg bg-purple-500 px-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">
                  <Orbit className="h-4 w-4" /> {flytoSendet ? 'Sende …' : 'POI starten'}
                </button>
              </div>
            </div>
          )}

          {flytoWahl && flytoZiel && (
            <div role="dialog" aria-label="FlyTo bestätigen" className="fixed inset-x-3 bottom-3 z-[1000] mx-auto max-h-[88dvh] max-w-md space-y-3 overflow-y-auto md:absolute md:z-30 rounded-xl border border-orange-400/60 bg-[#0e151c] p-3.5 text-sm shadow-xl">
              <div className="flex items-center justify-between">
                <b className="font-sans text-base">FlyTo zum markierten Punkt</b>
                <span className="font-mono text-xs text-white/60">{flytoZiel[0].toFixed(5)}, {flytoZiel[1].toFixed(5)}</span>
              </div>
              <div className="text-white/80">Entfernung zum Ziel: <b className="tabular-nums">{flytoEntfernung !== null ? meterText(flytoEntfernung) : '–'}</b></div>
              <label className="block space-y-1">
                <span className="text-white/70">Zielhöhe über Startpunkt (m, {FLYTO_HOEHE_MIN}–{FLYTO_HOEHE_MAX}) – jetzt {zahl(d.hoehe_m)} m</span>
                <input type="number" inputMode="decimal" min={FLYTO_HOEHE_MIN} max={FLYTO_HOEHE_MAX} step={1} value={flytoHoehe}
                  onChange={e => setFlytoHoehe(e.target.value)}
                  className="min-h-[48px] w-full rounded-lg border border-white/20 bg-black/40 px-3 text-base tabular-nums" />
              </label>
              {flytoHoeheOk && flytoHoeheZahl < 20 && (
                <p className="text-xs text-amber-200">Hinweis: Unter 20 m über dem Startpunkt steigt die Drohne laut DJI zuerst auf 20 m.</p>
              )}
              {!flytoHoeheOk && flytoHoehe.trim() !== '' && <p className="text-xs text-red-300">Höhe muss zwischen {FLYTO_HOEHE_MIN} und {FLYTO_HOEHE_MAX} m liegen.</p>}
              <label className="block space-y-1">
                <span className="flex justify-between text-white/70"><span>Höchstgeschwindigkeit</span><b className="tabular-nums text-white">{flytoTempo} m/s ({zahl(flytoTempo * 3.6)} km/h)</b></span>
                <input type="range" min={1} max={15} step={1} value={flytoTempo} onChange={e => setFlytoTempo(Number(e.target.value))} className="h-12 w-full" />
              </label>
              {flytoFehler && <p role="alert" className="rounded-lg bg-red-600 px-2.5 py-2 text-sm font-semibold text-white">{flytoFehler}</p>}
              <div className="grid grid-cols-2 gap-2">
                <Knopf onClick={flytoVerwerfen} gesperrt={flytoSendet}>Verwerfen</Knopf>
                <button type="button" onClick={flytoAbsenden} disabled={flytoSendet || !flytoHoeheOk || !bereit || !drohne.in_der_luft}
                  className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-2 text-sm font-bold text-black disabled:cursor-not-allowed disabled:opacity-40">
                  <Navigation className="h-4 w-4" /> {flytoSendet ? 'Sende …' : 'Jetzt hinfliegen'}
                </button>
              </div>
              <p className="text-[11px] text-white/50">Der Pilot behält die Kontrolle: Knüppelbewegung am Controller beendet FlyTo.</p>
            </div>
          )}

          {/* POI läuft: Status, Tempo (Richtung) einstellbar, gut sichtbarer Beenden-Knopf */}
          {pz && pz.laeuft && !(punktWahl && flytoZiel) && (
            <div role="status" className="absolute inset-x-3 bottom-3 z-30 mx-auto max-w-md space-y-2 rounded-xl border-2 border-purple-400 bg-[#0e151c] p-3 shadow-xl">
              <div className="flex items-center justify-between gap-3">
                <b className="font-sans text-base">{pz.status === 'angefordert' ? 'POI wird gestartet …' : 'POI läuft'}</b>
                <span className="text-xs text-white/60">{pz.text && pz.text !== 'POI läuft' ? pz.text : ''}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 font-mono tabular-nums">
                <div className="rounded bg-black/40 px-2 py-1">Radius <b>{pz.radius_m !== null && pz.radius_m !== undefined ? meterText(pz.radius_m) : '–'}</b></div>
                <div className="rounded bg-black/40 px-2 py-1">Tempo <b>{pz.tempo !== null && pz.tempo !== undefined ? `${zahl(Math.abs(pz.tempo), 1)} m/s ${pz.tempo < 0 ? '↻' : pz.tempo > 0 ? '↺' : ''}` : '–'}</b></div>
              </div>
              {pz.tempo_max ? (
                <label className="block space-y-0.5">
                  <span className="flex justify-between text-xs text-white/70"><span>↻ im Uhrzeigersinn</span><b className="text-white">{zahl(poiTempo ?? pz.tempo ?? 0, 1)} m/s</b><span>gegen ↺</span></span>
                  <input type="range" min={-pz.tempo_max} max={pz.tempo_max} step={0.5} className="h-12 w-full"
                    value={poiTempo ?? pz.tempo ?? 0} onChange={e => setPoiTempo(Number(e.target.value))}
                    onPointerUp={() => { if (poiTempo !== null) poiTempoSenden(poiTempo) }} onKeyUp={() => { if (poiTempo !== null) poiTempoSenden(poiTempo) }} />
                </label>
              ) : <p className="text-xs text-white/50">Tempo-Regler erscheint, sobald die Drohne ihre Höchstgeschwindigkeit meldet.</p>}
              {pz.alter_s > 15 && <p className="text-xs text-amber-300">Keine Statusmeldung der Drohne seit {Math.round(pz.alter_s)} s.</p>}
              <button type="button" onClick={poiBeenden} disabled={flytoSendet}
                className="flex min-h-[60px] w-full items-center justify-center gap-2 rounded-lg bg-red-600 text-lg font-bold text-white shadow disabled:opacity-60">
                <Square className="h-5 w-5" /> {flytoSendet ? 'Beende …' : 'POI beenden'}
              </button>
            </div>
          )}

          {/* Rückkehr: Bestätigungsdialog und laufende Rückkehr mit Abbruch */}
          {rthDialog && !rthLaeuft && (
            <div role="dialog" aria-label="Rückkehr bestätigen" className="fixed inset-x-3 bottom-3 z-[1000] mx-auto max-h-[88dvh] max-w-md space-y-3 overflow-y-auto md:absolute md:z-[35] rounded-xl border-2 border-amber-400 bg-[#0e151c] p-3.5 text-sm shadow-xl">
              <b className="block font-sans text-base">Drohne zurück zum Startpunkt schicken?</b>
              <p className="text-white/80">Die Drohne bricht ein laufendes FlyTo/POI ab und fliegt automatisch zurück (Entfernung zum Start: <b>{meterText(d.home_distanz_m ?? 0)}</b>, Akku {zahl(d.akku_prozent)} %).</p>
              <div className="grid grid-cols-2 gap-2">
                <Knopf onClick={() => setRthDialog(false)} gesperrt={rthSendet}>Nein</Knopf>
                <button type="button" onClick={rthAusloesen} disabled={rthSendet}
                  className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-lg bg-amber-400 px-2 text-sm font-bold text-black disabled:opacity-50">
                  <Home className="h-4 w-4" /> {rthSendet ? 'Sende …' : 'Ja, Rückkehr starten'}
                </button>
              </div>
            </div>
          )}
          {rthLaeuft && (
            <div role="status" className="absolute inset-x-3 bottom-3 z-[35] mx-auto max-w-md space-y-2 rounded-xl border-2 border-amber-400 bg-[#0e151c] p-3 shadow-xl">
              <div className="flex items-center justify-between gap-3">
                <b className="font-sans text-base">Rückkehr läuft</b>
                <span className="font-mono text-xs tabular-nums text-white/70">Entfernung zum Start {meterText(d.home_distanz_m ?? 0)}</span>
              </div>
              <button type="button" onClick={rthAbbrechen} disabled={rthSendet}
                className="flex min-h-[60px] w-full items-center justify-center gap-2 rounded-lg bg-red-600 text-lg font-bold text-white shadow disabled:opacity-60">
                <Square className="h-5 w-5" /> {rthSendet ? 'Breche ab …' : 'Rückkehr abbrechen (Drohne schwebt)'}
              </button>
            </div>
          )}

          {/* FlyTo läuft / gerade beendet: Status und gut sichtbarer Abbruch-Knopf */}
          {fz && (fz.laeuft || fz.status) && !(punktWahl && flytoZiel) && !rthLaeuft && !rthDialog && !pz?.laeuft && (
            <div role="status" className={`absolute inset-x-3 bottom-3 z-30 mx-auto max-w-md space-y-2 rounded-xl border-2 p-3 shadow-xl ${
              fz.laeuft ? 'border-sky-400 bg-[#0e151c]' : fz.status === 'wayline_ok' ? 'border-emerald-400 bg-[#0e151c]' : 'border-red-500 bg-[#0e151c]'}`}>
              <div className="flex items-center justify-between gap-3">
                <b className="font-sans text-base">{fz.laeuft ? 'FlyTo läuft' : fz.text || 'FlyTo beendet'}</b>
                {fz.laeuft && <span className="text-xs text-white/60">{fz.status === 'angefordert' ? 'wird gestartet …' : 'unterwegs'}</span>}
              </div>
              {fz.laeuft && (
                <div className="grid grid-cols-2 gap-2 font-mono tabular-nums">
                  <div className="rounded bg-black/40 px-2 py-1">Rest <b>{fz.rest_m !== null && fz.rest_m !== undefined ? meterText(fz.rest_m) : fz.entfernung_m ? `~${meterText(fz.entfernung_m)}` : '–'}</b></div>
                  <div className="rounded bg-black/40 px-2 py-1">Zeit <b>{fz.rest_s !== null && fz.rest_s !== undefined ? `${Math.round(fz.rest_s)} s` : '–'}</b></div>
                </div>
              )}
              {fz.laeuft && fz.alter_s > 10 && <p className="text-xs text-amber-300">Keine Statusmeldung der Drohne seit {Math.round(fz.alter_s)} s.</p>}
              {!fz.laeuft && fz.status === 'wayline_failed' && fz.result ? <p className="text-xs text-white/60">DJI-Fehlercode {fz.result}</p> : null}
              {fz.laeuft && (
                <button type="button" onClick={flytoAbbrechen} disabled={flytoSendet}
                  className="flex min-h-[60px] w-full items-center justify-center gap-2 rounded-lg bg-red-600 text-lg font-bold text-white shadow disabled:opacity-60">
                  <Square className="h-5 w-5" /> {flytoSendet ? 'Breche ab …' : 'FlyTo abbrechen'}
                </button>
              )}
            </div>
          )}

          {/* IR-Messung: Werkzeug zum Tippen/Ziehen im Bild und Messwerte mit Position */}
          {!karteGross && bildR.b > 0 && (
            <div className="absolute z-30" style={{ left: bildR.x, top: bildR.y, width: bildR.b, height: bildR.h, pointerEvents: (werkzeug === 'ir_punkt' || werkzeug === 'ir_flaeche') ? 'auto' : 'none', touchAction: 'none' }}
              onPointerDown={irDown} onPointerMove={irMove} onPointerUp={irUp} onPointerCancel={() => setZiehen(null)}>
              {(werkzeug === 'ir_punkt' || werkzeug === 'ir_flaeche') && (
                <div className="absolute inset-0 cursor-crosshair border border-dashed border-amber-300/70 bg-amber-300/5">
                  <span className="absolute left-1/2 top-1 -translate-x-1/2 rounded bg-black/70 px-2 py-1 text-xs font-semibold text-amber-200">
                    {werkzeug === 'ir_punkt' ? 'Messpunkt antippen' : 'Rahmen für die Messfläche ziehen'}
                  </span>
                </div>
              )}
              {ziehen && werkzeug === 'ir_flaeche' && (
                <div className="absolute border-2 border-amber-300 bg-amber-300/10"
                  style={{ left: `${Math.min(ziehen.x0, ziehen.x1) * 100}%`, top: `${Math.min(ziehen.y0, ziehen.y1) * 100}%`,
                    width: `${Math.abs(ziehen.x1 - ziehen.x0) * 100}%`, height: `${Math.abs(ziehen.y1 - ziehen.y0) * 100}%` }} />
              )}
              {irLinse && irMessung}
            </div>
          )}

          {/* NOTSTOPP: immer sichtbar, solange die Steuerung erteilt ist; eine Berührung genügt (auch Leertaste / Gamepad B) */}
          {steuerungErteilt && (
            <button type="button" onClick={hand.notstopp} aria-label="Notstopp" title="Notstopp: Drohne sofort anhalten (Leertaste, Gamepad B)"
              className={`absolute right-3 top-20 z-40 flex h-[88px] w-[88px] flex-col items-center justify-center rounded-full border-4 border-white bg-red-600 text-[13px] font-extrabold leading-tight text-white shadow-xl active:scale-95 ${notstoppSendet ? 'animate-pulse' : ''}`}>
              <OctagonX className="h-7 w-7" /> NOTSTOPP
            </button>
          )}

          {/* Handsteuerung aktiv: Hinweis + Wisch-Sticks unten links/rechts (nur bei großem Bild) */}
          {hand.an && (
            <div className="pointer-events-none absolute left-1/2 top-3 z-[36] -translate-x-1/2 rounded-full bg-amber-400 px-3 py-1 text-xs font-bold text-black shadow">
              HANDSTEUERUNG · {hand.stufe} m/s
            </div>
          )}
          {hand.an && touchSticks && !karteGross && <TouchSticks onWert={hand.touchSetzen} />}

          {/* Einblendungen im Bild (nur wenn das Bild groß ist) */}
          {!karteGross && (
            <div className="pointer-events-none absolute inset-0 z-10 font-mono text-xs">
              {streamAktiv && (
                <div className="absolute left-3 top-3 flex gap-2">
                  <span className="rounded bg-black/55 px-2 py-1">{linsenName(linse)}{kam.zoom_faktor && linse === 'zoom' ? ` · ${zahl(kam.zoom_faktor, 1)}×` : ''}{kam.ir_zoom_faktor && linse === 'ir' ? ` · ${zahl(kam.ir_zoom_faktor, 0)}×` : ''}</span>
                  {uebertragung && (() => {
                    const v = uebertragung.verzoegerung_ms
                    const farbe = v === null ? 'bg-black/55' : v > 3000 ? 'bg-red-600/90' : v > 1500 ? 'bg-amber-400/90 text-black' : 'bg-black/55'
                    return <span className={`rounded px-2 py-1 ${farbe}`}>Bild {v === null ? '–' : `≈ ${zahl(v / 1000, 1)} s`} · {uebertragung.weg === 'webrtc' ? 'WebRTC' : 'HLS'}{v !== null && v > 3000 ? ' · Bild verzögert – nur langsam steuern' : ''}</span>
                  })()}
                  {kam.aufnahme === 1 && <span className="rounded bg-red-600/90 px-2 py-1 font-sans font-semibold">● Aufnahme {kam.aufnahme_s ? minuten(kam.aufnahme_s) : ''}</span>}
                </div>
              )}
              {(kam.ir_max_c !== undefined || kam.laser_distanz_m || irText) && (
                <div className="absolute left-3 top-12 space-y-1">
                  {kam.ir_max_c !== undefined && <div className="rounded bg-black/55 px-2 py-1">IR max {zahl(kam.ir_max_c, 1)} °C · min {zahl(kam.ir_min_c, 1)} °C</div>}
                  {!!kam.laser_distanz_m && <div className="rounded bg-black/55 px-2 py-1">Laser {zahl(kam.laser_distanz_m, 1)} m</div>}
                  {irText}
                </div>
              )}
              {hms.length > 0 && (
                <div className="absolute right-3 top-3 max-w-[60%] space-y-1 text-right">
                  {hms.map(h => (
                    <div key={h.code} className={`rounded px-2 py-1 font-sans ${h.stufe >= 2 ? 'bg-red-600/85' : 'bg-amber-500/85 text-black'}`}>
                      {h.stufe >= 2 ? 'Warnung' : 'Achtung'}: {h.meldung || h.code}
                    </div>
                  ))}
                </div>
              )}
              <div className={`absolute left-3 grid grid-cols-[auto_auto] ${hand.an && touchSticks ? 'bottom-[190px]' : 'bottom-3'} gap-x-3 gap-y-0.5 rounded bg-black/55 px-2.5 py-1.5 tabular-nums`}>
                <span className="text-white/60">H Start</span><b className={(d.hoehe_m ?? 0) > 120 ? 'text-red-300' : ''}>{zahl(d.hoehe_m)} m</b>
                <span className="text-white/60">Entf.</span><b>{zahl(d.home_distanz_m)} m</b>
                <span className="text-white/60">Tempo</span><b>{zahl(d.speed_h !== undefined ? d.speed_h * 3.6 : undefined)} km/h</b>
                <span className="text-white/60">Rest</span><b className={akkuKritisch ? 'text-red-300' : ''}>{minuten(d.restflugzeit_s)}</b>
                <span className="text-white/60">GPS</span><b className={(d.gps_satelliten ?? 99) < 8 ? 'text-amber-300' : ''}>{zahl(d.gps_satelliten)}</b>
                {drcAktiv && st!.osd.gimbal_pitch !== undefined && <><span className="text-white/60">Gimbal</span><b>{zahl(st!.osd.gimbal_pitch)}°</b></>}
              </div>
            </div>
          )}
        </div>

        {/* Bedienleiste */}
        <div className="flex max-h-[45%] w-full shrink-0 flex-col gap-2.5 overflow-y-auto border-t border-white/10 bg-[#17222d] p-3 md:max-h-none md:w-[280px] md:border-l md:border-t-0">
          <div className="sticky top-0 z-10 -mx-3 -mt-3 space-y-1.5 border-b border-white/10 bg-[#17222d] px-3 pb-2.5 pt-3">
            <Ueberschrift>Steuerung</Ueberschrift>
            <div className="flex items-start gap-2 text-sm">
              <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${steuerFarbe}`} />
              <span>{steuerText}</span>
            </div>
            {st?.status === 'erteilt' || st?.status === 'angefragt' ? (
              <Knopf gefahr onClick={() => steuerung('abgeben')} gesperrt={aktionLaeuft}>
                <ShieldOff className="h-4 w-4" /> {st.status === 'erteilt' ? 'Steuerung abgeben' : 'Anfrage zurückziehen'}
              </Knopf>
            ) : (
              <Knopf onClick={() => steuerung('anfragen')} gesperrt={aktionLaeuft || veraltet}
                titel="Am Controller erscheint ein Pop-up; erst wenn der Pilot zustimmt, kann das Cockpit steuern.">
                <Hand className="h-4 w-4" /> {st?.status === 'abgelehnt' ? 'Erneut anfragen' : 'Steuerung anfragen'}
              </Knopf>
            )}
          </div>

          <div className="rounded-lg border border-dark-border bg-dark-bg-tertiary">
            <button type="button" onClick={() => setTelemetrieOffen(o => !o)} aria-expanded={telemetrieOffen}
              className="flex min-h-[48px] w-full items-center justify-between gap-2 px-3 text-sm font-semibold text-dark-text">
              <span>Telemetrie{hms.length > 0 && <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${hms.some(h => h.stufe >= 2) ? 'bg-red-600 text-white' : 'bg-amber-500 text-black'}`}>{hms.length} HMS</span>}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${telemetrieOffen ? 'rotate-180' : ''}`} />
            </button>
            {telemetrieOffen && (
              <div className="space-y-2 border-t border-white/10 p-3 text-sm">
                {hms.length > 0 && (
                  <ul className="space-y-1">
                    {hms.map(h => (
                      <li key={h.code} className={`rounded px-2 py-1 ${h.stufe >= 2 ? 'bg-red-600/85 text-white' : 'bg-amber-500/85 text-black'}`}>
                        <b>{h.stufe >= 2 ? 'Warnung' : 'Achtung'}:</b> {h.meldung || 'ohne Klartext'} <span className="opacity-70">({h.code})</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 tabular-nums">
                  {([
                    ['Höhe über Start', `${zahl(d.hoehe_m, 1)} m`, (d.hoehe_m ?? 0) > 120],
                    ['Höhe absolut', `${zahl(d.hoehe_abs_m)} m`, false],
                    ['Geschwindigkeit', `${zahl(d.speed_h !== undefined ? d.speed_h * 3.6 : undefined, 1)} km/h`, false],
                    ['Steigen / Sinken', `${zahl(d.speed_v, 1)} m/s`, false],
                    ['Entfernung Start', `${zahl(d.home_distanz_m)} m`, false],
                    ['Kurs', `${zahl(d.kurs !== undefined ? (d.kurs + 360) % 360 : undefined)}°`, false],
                    ['Akku', `${zahl(d.akku_prozent)} %`, akkuKritisch],
                    ['Restflugzeit', dauer(d.restflugzeit_s), akkuKritisch],
                    ['Flugzeit', dauer(flugzeit), false],
                    ['GPS-Satelliten', zahl(d.gps_satelliten), (d.gps_satelliten ?? 99) < 8],
                    ...(d.rtk_satelliten !== undefined && d.rtk_satelliten !== null ? [['RTK-Satelliten', zahl(d.rtk_satelliten), false] as const] : []),
                    ['Funkqualität', d.funk_qualitaet !== undefined && d.funk_qualitaet !== null ? `${d.funk_qualitaet}/5` : '–', (d.funk_qualitaet ?? 5) <= 1],
                    ['Wind (geschätzt)', d.wind_geschaetzt_ms != null ? `${zahl(d.wind_geschaetzt_ms, 1)} m/s${d.wind_richtung_grad != null ? ` · ${['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'][Math.round(d.wind_richtung_grad / 45) % 8]}` : ''}` : '–', false],
                  ] as readonly (readonly [string, string, boolean])[]).map(([k, v, warn]) => (
                    <div key={k} className="contents">
                      <span className="text-dark-text-muted">{k}</span>
                      <b className={`text-right ${warn ? 'text-red-300' : 'text-dark-text'}`}>{v}</b>
                    </div>
                  ))}
                </div>
                {d.akkus && d.akkus.length > 0 && (
                  <div className="space-y-1 border-t border-white/10 pt-2">
                    <Ueberschrift>Akkus</Ueberschrift>
                    {d.akkus.map(a => (
                      <div key={a.sn} className="text-xs">
                        <div className="font-mono text-dark-text-muted">{a.sn}</div>
                        <div>{zahl(a.prozent)} % · {zahl(a.ladezyklen)} Ladezyklen{a.temperatur !== undefined && a.temperatur !== null ? ` · ${zahl(a.temperatur, 1)} °C` : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Ueberschrift>Live-Video</Ueberschrift>
            {lb?.freigabe && <FreigabeAnzeige freigabe={lb.freigabe} />}
            <div className="flex items-center gap-2 text-sm">
              <span className={`h-2.5 w-2.5 rounded-full ${lb?.laeuft ? 'bg-emerald-400' : streamAktiv ? 'bg-amber-400' : 'bg-slate-500'}`} />
              {lb?.laeuft ? 'läuft' : streamAktiv ? 'wird aufgebaut …' : !lb?.anforderbar ? 'keine Kameras gemeldet'
                : autoFehler ? 'Start fehlgeschlagen' : wirdAngefordert ? 'wird angefordert …' : 'beendet'}
            </div>
            {(autoFehler || (!streamAktiv && !wirdAngefordert && lb?.anforderbar)) && (
              <>
                {autoFehler && <p className="text-[11px] text-red-300">{autoFehler}</p>}
                <Knopf onClick={() => { setAutoFehler(null); steuern('start') }} gesperrt={aktionLaeuft}><Video className="h-4 w-4" /> Erneut versuchen</Knopf>
              </>
            )}
          </div>

          <div className="space-y-1.5">
            <Ueberschrift>Linse</Ueberschrift>
            <div className="grid grid-cols-3 gap-1.5">
              {linsen.map(l => (
                <Knopf key={l} aktiv={linse === l} gesperrt={!streamAktiv || aktionLaeuft || linse === l} onClick={() => steuern(`linse/${l}`)}>
                  {linsenName(l)}
                </Knopf>
              ))}
            </div>
            <p className="text-[11px] text-white/50">Ändert auch das Bild auf der Fernsteuerung des Piloten.</p>
          </div>

          <div className="space-y-1.5">
            <Ueberschrift>Zoom{zoomLinse ? ` · ${zoomLinse === 'zoom' ? 'Zoom-Kamera' : 'IR-Kamera'}` : ''}</Ueberschrift>
            {zoomLinse ? (
              <>
                <div className="flex items-center gap-2">
                  <input type="range" min={0} max={100} step={1} aria-label="Zoomfaktor" disabled={!bereit || befehlLaeuft}
                    title={kameraTitel} className="h-12 flex-1 disabled:opacity-40"
                    value={zoomPos ?? (zoomIst ? zoomZuPos(zoomIst, zoomMax) : 0)}
                    onChange={e => setZoomPos(Number(e.target.value))}
                    onPointerUp={() => { if (zoomPos !== null) { kameraBefehl('zoom', { linse: zoomLinse, faktor: posZuZoom(zoomPos, zoomMax) }); setZoomPos(null) } }}
                    onKeyUp={() => { if (zoomPos !== null) { kameraBefehl('zoom', { linse: zoomLinse, faktor: posZuZoom(zoomPos, zoomMax) }); setZoomPos(null) } }} />
                  <span className="w-14 text-right font-mono text-sm tabular-nums">{zoomAnzeige ? `${zahl(zoomAnzeige, 1)}×` : '–'}</span>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {ZOOM_PRESETS[zoomLinse].map(f => (
                    <Knopf key={f} gesperrt={!bereit || befehlLaeuft} titel={kameraTitel} onClick={() => kameraBefehl('zoom', { linse: zoomLinse, faktor: f })}>{f}×</Knopf>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-xs text-white/50"><ZoomIn className="h-4 w-4 shrink-0" /> Zoom nur mit Zoom- oder IR-Linse (oben wählen).</div>
            )}
          </div>

          <div className="space-y-1.5">
            <Ueberschrift>Gimbal</Ueberschrift>
            <GimbalFeld gesperrt={!bereit} onBewegen={gimbalBewegen} />
            <p className="text-center text-[11px] text-white/50">{bereit ? 'Halten und ziehen – Loslassen hält an.' : OHNE_STEUERUNG}</p>
            <div className="grid grid-cols-2 gap-1.5">
              <Knopf gesperrt={!bereit || befehlLaeuft} titel={kameraTitel} onClick={() => kameraBefehl('gimbal_reset', { modus: 0 })}><RotateCcw className="h-4 w-4" /> Zurücksetzen</Knopf>
              <Knopf gesperrt={!bereit || befehlLaeuft} titel={kameraTitel} onClick={() => kameraBefehl('gimbal_reset', { modus: 1 })}><ArrowDownToLine className="h-4 w-4" /> Nach unten</Knopf>
            </div>
          </div>

          <div className="space-y-1.5">
            <Ueberschrift>Aufnahme</Ueberschrift>
            <div className="grid grid-cols-2 gap-1.5">
              <Knopf gesperrt={!bereit || befehlLaeuft} titel={kameraTitel} onClick={() => kameraBefehl('foto')}><Camera className="h-4 w-4" /> Foto</Knopf>
              <Knopf gefahr={aufnahme} gesperrt={!bereit || befehlLaeuft} titel={kameraTitel} onClick={() => kameraBefehl(aufnahme ? 'video_stop' : 'video_start')}>
                {aufnahme ? <><Square className="h-4 w-4" /> Stopp</> : <><Video className="h-4 w-4" /> Video</>}
              </Knopf>
            </div>
          </div>

          <div className="space-y-1.5">
            <Ueberschrift>Blick &amp; Messung</Ueberschrift>
            <div className="grid grid-cols-2 gap-1.5">
              <Knopf aktiv={werkzeug === 'lookat'} gesperrt={!bereit || befehlLaeuft}
                titel={kameraTitel ?? 'Drohne auf einen Punkt der Karte ausrichten (Zielhöhe = Starthöhe)'}
                onClick={() => setWerkzeug(w => w === 'lookat' ? null : 'lookat')}><Crosshair className="h-4 w-4" /> Look-At</Knopf>
              <Knopf aktiv={werkzeug === 'ir_punkt'} gesperrt={!bereit || befehlLaeuft || !irLinse}
                titel={kameraTitel ?? (irLinse ? 'Temperatur an einem Punkt im Bild messen' : 'Dafür die Linse IR wählen')}
                onClick={() => { if (karteGross) setKarteGross(false); setWerkzeug(w => w === 'ir_punkt' ? null : 'ir_punkt') }}><Thermometer className="h-4 w-4" /> IR-Punkt</Knopf>
              <Knopf aktiv={werkzeug === 'ir_flaeche'} gesperrt={!bereit || befehlLaeuft || !irLinse}
                titel={kameraTitel ?? (irLinse ? 'Rahmen im Bild ziehen: Max/Min-Temperatur der Fläche' : 'Dafür die Linse IR wählen')}
                onClick={() => { if (karteGross) setKarteGross(false); setWerkzeug(w => w === 'ir_flaeche' ? null : 'ir_flaeche') }}><Thermometer className="h-4 w-4" /> IR-Fläche</Knopf>
              <Knopf gesperrt={!bereit || befehlLaeuft || irModus === 0} titel={kameraTitel ?? 'IR-Messung beenden'}
                onClick={async () => { if (await kameraBefehl('ir_aus')) { setIrZiel(null); setWerkzeug(null) } }}><X className="h-4 w-4" /> Messung aus</Knopf>
            </div>
            {werkzeug && (
              <p className="rounded-lg border border-amber-300/30 bg-amber-300/10 px-2.5 py-2 text-xs text-amber-100">
                {werkzeug === 'lookat' ? 'Punkt auf der kleinen Karte antippen (Karte ggf. mit „Karte groß“ vergrößern).'
                  : werkzeug === 'ir_punkt' ? 'Messpunkt im Videobild antippen.' : 'Im Videobild einen Rahmen aufziehen (oder antippen für eine Standardfläche).'}
                {' '}<button type="button" className="underline" onClick={() => setWerkzeug(null)}>Abbrechen</button>
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Ueberschrift>Flug</Ueberschrift>
            <div className="grid grid-cols-2 gap-1.5">
              <Knopf aktiv={flytoWahl} gesperrt={!!flyPoiGesperrtTitel || befehlLaeuft || flytoSendet}
                titel={flyPoiGesperrtTitel ?? 'Punkt auf der Karte wählen, Drohne fliegt dorthin (FlyTo)'}
                onClick={() => (flytoWahl ? flytoVerwerfen() : flytoStarten())}><Navigation className="h-4 w-4" /> FlyTo</Knopf>
              <Knopf aktiv={poiWahl} gesperrt={!!flyPoiGesperrtTitel || befehlLaeuft || flytoSendet}
                titel={flyPoiGesperrtTitel ?? 'Mittelpunkt auf der Karte wählen, Drohne kreist darum (POI)'}
                onClick={() => (poiWahl ? flytoVerwerfen() : poiStarten())}><Orbit className="h-4 w-4" /> POI</Knopf>
              <Knopf gefahr gesperrt={!drohne.in_der_luft || !bereit || rthLaeuft || rthSendet} aktiv={rthDialog}
                titel={!drohne.in_der_luft ? 'Nur möglich, solange die Drohne fliegt.' : !bereit ? OHNE_STEUERUNG : 'Drohne fliegt automatisch zum Startpunkt zurück (mit Bestätigung)'}
                onClick={() => { flytoVerwerfen(); setRthDialog(true) }}><Home className="h-4 w-4" /> Rückkehr</Knopf>
            </div>
          </div>

          <HandsteuerungPanel an={hand.an} sperrTitel={handSperre ?? undefined} stufe={hand.stufe} werte={hand.werte} fehler={hand.fehler}
            gamepad={hand.gamepad} latenzMs={hand.latenzMs ?? st?.latenz_ms} socketOffen={hand.socketOffen} verzoegerungMs={uebertragung?.verzoegerung_ms ?? null}
            touchSticks={touchSticks} onTouchSticks={setTouchSticks} onEin={hand.einschalten} onAus={() => hand.ausschalten('Handsteuerung ausgeschaltet.', false)} onStufe={hand.setStufe} />

          <p className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-2 text-[11px] leading-snug text-white/55">
            FlyTo, POI und Rückkehr sind nur bei freigegebener Steuerung und fliegender Drohne möglich. Jeder Befehl wird im Steuerungsprotokoll festgehalten.
            Der Pilot kann die Freigabe in DJI Pilot 2 jederzeit zurückholen.
          </p>

          <div className="mt-auto space-y-1.5">
            <Knopf onClick={() => setKarteGross(g => !g)}><MapIcon className="h-4 w-4" /> {karteGross ? 'Bild groß' : 'Karte groß'}</Knopf>
            {(aktionFehler || lb?.meldung) && (
              <div className={`rounded-lg border px-2.5 py-2 text-xs ${aktionFehler ? 'border-red-500/30 bg-red-500/10 text-red-200' : 'border-white/10 text-white/60'}`}>
                {aktionFehler || lb?.meldung}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
